/**
 * @module  public/js/core/endpoints-service.js
 * @summary Generates environment-aware URLs for all backend and frontend routes.
 *
 * @description
 * Exposes `envRoots` maps and an `EndpointService` that auto-detects dev vs prod
 * and serialises query-params.  Consumers import the singleton `endpoints`
 * or create fresh instances for testing.
 *
 * ## Key Features
 * – Central registry of cloud-function roots for dev and production.
 * – `EndpointService` builds URLs with query-param helpers.
 * – Auto-detects environment from `window.location.hostname`.
 * – Provides uniform `fetch()` wrapper for dependency injection.
 * – Exports a ready-to-use singleton (`endpoints`).
 * – Contact note helpers support `{ crm: 'local' }` for mirror-backed mode.
 *
 * ## Public API
 * – `envRoots` — constant map of environment → service roots.
 * – `class EndpointService(config)` — configurable URL builder.
 * – `getFrontendUrl(path, params)` — build link into SPA with QS params.
 * – `fetch(url, opts)` — injected fetch wrapper.
 * – `endpoints` — default `EndpointService` instance (export default).
 *
 * ## Dependencies
 * (none)
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

import {
  initializeApp,
  getApps,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-app.js';

export const envRoots = {
  /* ────────────────────────────
   * Local staging / feature-dev
   * (hash = zmihliczcq)
   * ──────────────────────────── */
  dev: {
    frontend: 'https://dev-leadepic-for-linkedin.web.app',

    callPerplexityApiForAccounts:
      'https://callperplexityapiforaccounts-zmihliczcq-uc.a.run.app',
    callPerplexityApiForAccountsStart:
      'https://callperplexityapiforaccountsstart-zmihliczcq-uc.a.run.app',
    callPerplexityApiForAccountsCheck:
      'https://callperplexityapiforaccountscheck-zmihliczcq-uc.a.run.app',

    generateInstallUrl: 'https://generateinstallurl-zmihliczcq-uc.a.run.app',
    handleHubspotAppOAuth:
      'https://handlehubspotappoauth-zmihliczcq-uc.a.run.app',

    getAccounts: 'https://getaccounts-zmihliczcq-uc.a.run.app',
    getAccountsByOwner: 'https://getaccountsbyowner-zmihliczcq-uc.a.run.app',
    getAccountDetails: 'https://getaccountdetails-zmihliczcq-uc.a.run.app',
    getAccountDeals: 'https://getaccountdeals-zmihliczcq-uc.a.run.app',
    updateAccountDetails:
      'https://updateaccountdetails-zmihliczcq-uc.a.run.app',
    createCompany: 'https://createcompany-zmihliczcq-uc.a.run.app',

    getContactDetails: 'https://getcontactdetails-zmihliczcq-uc.a.run.app',
    updateContactDetails:
      'https://updatecontactdetails-zmihliczcq-uc.a.run.app',
    getAccountContacts: 'https://getaccountcontacts-zmihliczcq-uc.a.run.app',
    saveContactNotes: 'https://savecontactnotes-zmihliczcq-uc.a.run.app',
    getContactNotes: 'https://getcontactnotes-zmihliczcq-uc.a.run.app',
    saveContactReport: 'https://savecontactreport-zmihliczcq-uc.a.run.app',
    createContact: 'https://createcontact-zmihliczcq-uc.a.run.app',

    getDealDetails: 'https://getdealdetails-zmihliczcq-uc.a.run.app',
    updateDealDetails: 'https://updatedealdetails-zmihliczcq-uc.a.run.app',
    createDeal: 'https://createdeal-zmihliczcq-uc.a.run.app',
    associateContactWithDeal:
      'https://associatecontactwithdeal-zmihliczcq-uc.a.run.app',
    saveCompanyReport: 'https://savecompanyreport-zmihliczcq-uc.a.run.app',
    saveHubspotContactReport:
      'https://savehubspotcontactreport-zmihliczcq-uc.a.run.app',
    saveHubspotCompanyReport:
      'https://savehubspotcompanyreport-zmihliczcq-uc.a.run.app',
    backfillHubspotPortalId:
      'https://backfillhubspotportalid-zmihliczcq-uc.a.run.app',

    createLineItemForDeal:
      'https://createlineitemfordeal-zmihliczcq-uc.a.run.app',
    updateLineItem: 'https://updatelineitem-zmihliczcq-uc.a.run.app',
    deleteLineItem: 'https://deletelineitem-zmihliczcq-uc.a.run.app',
    getLineItemsForDeal: 'https://getlineitemsfordeal-zmihliczcq-uc.a.run.app',

    getPipelines: 'https://getpipelines-zmihliczcq-uc.a.run.app',

    getUserTasks: 'https://getusertasks-zmihliczcq-uc.a.run.app',
    manageUserTask: 'https://manageusertask-zmihliczcq-uc.a.run.app',
    taskMutations: 'https://taskmutations-zmihliczcq-uc.a.run.app',

    sendDailyNews: 'https://senddailynews-zmihliczcq-uc.a.run.app',
    sendNewsForUser: 'https://sendnewsforuser-zmihliczcq-uc.a.run.app',

    hubspotAccountsCard: 'https://hubspotaccountscard-zmihliczcq-uc.a.run.app',
    hubspotContactsCard: 'https://hubspotcontactscard-zmihliczcq-uc.a.run.app',
    hubspotDealsCard: 'https://hubspotdealscard-zmihliczcq-uc.a.run.app',
    hubspotListSequences:
      'https://hubspotlistsequences-zmihliczcq-uc.a.run.app',
    hubspotEnrollSequence:
      'https://hubspotenrollsequence-zmihliczcq-uc.a.run.app',

    generateAuthUrl: 'https://generateauthurl-zmihliczcq-uc.a.run.app',
    handleAuthCallback: 'https://handleauthcallback-zmihliczcq-uc.a.run.app',

    generateEmailAuthUrl:
      'https://generateemailauthurl-zmihliczcq-uc.a.run.app',
    handleEmailAuthCallback:
      'https://handleemailauthcallback-zmihliczcq-uc.a.run.app',
    sendUserEmail: 'https://senduseremail-zmihliczcq-uc.a.run.app',
    clearEmailOAuth: 'https://clearemailoauth-zmihliczcq-uc.a.run.app',
    messagingSend: 'https://messagingsend-zmihliczcq-uc.a.run.app',

    getTenantClaim: 'https://gettenantclaim-zmihliczcq-uc.a.run.app',
    setTenantClaim: 'https://settenantclaim-zmihliczcq-uc.a.run.app',
    createUserWithReset:
      'https://us-central1-topsail-development.cloudfunctions.net/createUserWithReset',
    bootstrapTenant: 'https://bootstraptenant-zmihliczcq-uc.a.run.app',
    grantSuperAdmin: '/grantSuperAdmin',
    getSuperadminPromptTemplateDefaults: '/getSuperadminPromptTemplateDefaults',
    listSuperadminTenants: '/listSuperadminTenants',
    updateTenantServiceOffering: '/updateTenantServiceOffering',
    listSuperadminMonthlyReports: '/listSuperadminMonthlyReports',
    getSuperadminTenantTaskOutcomeReport:
      '/getSuperadminTenantTaskOutcomeReport',
    getSuperadminTenantDealAttributionReport:
      '/getSuperadminTenantDealAttributionReport',
    requestForceLogoutAllUsers: '/requestForceLogoutAllUsers',
    listGuidanceCampaigns: '/listGuidanceCampaigns',
    saveGuidanceCampaignDraft: '/saveGuidanceCampaignDraft',
    publishGuidanceCampaign: '/publishGuidanceCampaign',
    archiveGuidanceCampaign: '/archiveGuidanceCampaign',
    deleteGuidanceCampaign: '/deleteGuidanceCampaign',
    resolveGuidanceCampaigns: '/resolveGuidanceCampaigns',
    recordGuidanceCampaignProgress: '/recordGuidanceCampaignProgress',
    setSuperadminPromptTemplateDefaults: '/setSuperadminPromptTemplateDefaults',
    createTenantDeletionPreview: '/createTenantDeletionPreview',
    getTenantDeletionJob: '/getTenantDeletionJob',
    confirmTenantQuarantine: '/confirmTenantQuarantine',
    startTenantPurge: '/startTenantPurge',

    initiateCall:
      'https://us-central1-topsailrevenue.cloudfunctions.net/twilioInitiateCall',
    zoomCallInitiate:
      'https://us-central1-topsailrevenue.cloudfunctions.net/zoomCallInitiate',
    systemCallInitiate:
      'https://us-central1-topsailrevenue.cloudfunctions.net/systemCallInitiate',
    zoomStatus:
      'https://us-central1-topsailrevenue.cloudfunctions.net/zoomStatus',

    getMeetingEngagements:
      'https://getmeetingengagements-zmihliczcq-uc.a.run.app',
    saveMeetingEngagement:
      'https://savemeetingengagement-zmihliczcq-uc.a.run.app',

    linkedInCompanyPeopleCSE:
      'https://linkedincompanypeoplecse-zmihliczcq-uc.a.run.app',
    callBrightDataScraperProfile:
      'https://callbrightdatascraperprofile-zmihliczcq-uc.a.run.app',

    getProducts: 'https://getproducts-zmihliczcq-uc.a.run.app',
    getCollectionStats: '/getCollectionStats',
    getBqMeta: '/getBqMeta',
    runBqReport: '/runBqReport',
    runMarketingAnalyticsDashboard: '/runMarketingAnalyticsDashboard',
    runMarketingAnalyticsBreakdown: '/runMarketingAnalyticsBreakdown',
    getMarketingNotificationSettings:
      'https://us-central1-topsail-development.cloudfunctions.net/getMarketingNotificationSettings',
    setMarketingNotificationSettings:
      'https://us-central1-topsail-development.cloudfunctions.net/setMarketingNotificationSettings',
    listSuperadminMarketingPosts: '/listSuperadminMarketingPosts',
    upsertSuperadminMarketingPost: '/upsertSuperadminMarketingPost',
    deleteSuperadminMarketingPost: '/deleteSuperadminMarketingPost',
    publishSuperadminMarketingPostNow: '/publishSuperadminMarketingPostNow',
    startSuperadminMarketingOAuth: '/startSuperadminMarketingOAuth',
    handleSuperadminMarketingOAuthCallback:
      '/handleSuperadminMarketingOAuthCallback',
    refreshSuperadminMarketingPostMetrics:
      '/refreshSuperadminMarketingPostMetrics',
    taskForecast: '/taskForecast',
    taskForecastRange: '/taskForecastRange',
    leadInboxCountsRange: '/leadInboxCountsRange',
  },

  /* ────────────────────────────
   * Production (topsailrevenue)
   * hash = gemiumdlca
   * ──────────────────────────── */
  production: {
    frontend: 'https://topsailrevenue.web.app/',

    callPerplexityApiForAccounts:
      'https://callperplexityapiforaccounts-gemiumdlca-uc.a.run.app',
    callPerplexityApiForAccountsStart:
      'https://callperplexityapiforaccountsstart-gemiumdlca-uc.a.run.app',
    callPerplexityApiForAccountsCheck:
      'https://callperplexityapiforaccountscheck-gemiumdlca-uc.a.run.app',

    generateInstallUrl: 'https://generateinstallurl-gemiumdlca-uc.a.run.app',
    handleHubspotAppOAuth:
      'https://handlehubspotappoauth-gemiumdlca-uc.a.run.app',

    getAccounts: 'https://getaccounts-gemiumdlca-uc.a.run.app',
    getAccountsByOwner: 'https://getaccountsbyowner-gemiumdlca-uc.a.run.app',
    getAccountDetails: 'https://getaccountdetails-gemiumdlca-uc.a.run.app',
    getAccountDeals: 'https://getaccountdeals-gemiumdlca-uc.a.run.app',
    updateAccountDetails:
      'https://updateaccountdetails-gemiumdlca-uc.a.run.app',
    createCompany: 'https://createcompany-gemiumdlca-uc.a.run.app',

    getContactDetails: 'https://getcontactdetails-gemiumdlca-uc.a.run.app',
    updateContactDetails:
      'https://updatecontactdetails-gemiumdlca-uc.a.run.app',
    getAccountContacts: 'https://getaccountcontacts-gemiumdlca-uc.a.run.app',
    saveContactNotes: 'https://savecontactnotes-gemiumdlca-uc.a.run.app',
    getContactNotes: 'https://getcontactnotes-gemiumdlca-uc.a.run.app',
    saveContactReport: 'https://savecontactreport-gemiumdlca-uc.a.run.app',
    createContact: 'https://createcontact-gemiumdlca-uc.a.run.app',

    getDealDetails: 'https://getdealdetails-gemiumdlca-uc.a.run.app',
    updateDealDetails: 'https://updatedealdetails-gemiumdlca-uc.a.run.app',
    createDeal: 'https://createdeal-gemiumdlca-uc.a.run.app',
    associateContactWithDeal:
      'https://associatecontactwithdeal-gemiumdlca-uc.a.run.app',
    saveCompanyReport: 'https://savecompanyreport-gemiumdlca-uc.a.run.app',
    saveHubspotContactReport:
      'https://savehubspotcontactreport-gemiumdlca-uc.a.run.app',
    saveHubspotCompanyReport:
      'https://savehubspotcompanyreport-gemiumdlca-uc.a.run.app',
    backfillHubspotPortalId:
      'https://backfillhubspotportalid-gemiumdlca-uc.a.run.app',

    createLineItemForDeal:
      'https://createlineitemfordeal-gemiumdlca-uc.a.run.app',
    updateLineItem: 'https://updatelineitem-gemiumdlca-uc.a.run.app',
    deleteLineItem: 'https://deletelineitem-gemiumdlca-uc.a.run.app',
    getLineItemsForDeal: 'https://getlineitemsfordeal-gemiumdlca-uc.a.run.app',

    getPipelines: 'https://getpipelines-gemiumdlca-uc.a.run.app',

    getUserTasks: 'https://getusertasks-gemiumdlca-uc.a.run.app',
    manageUserTask: 'https://manageusertask-gemiumdlca-uc.a.run.app',
    taskMutations: 'https://taskmutations-gemiumdlca-uc.a.run.app',

    sendDailyNews: 'https://senddailynews-gemiumdlca-uc.a.run.app',
    sendNewsForUser: 'https://sendnewsforuser-gemiumdlca-uc.a.run.app',

    hubspotAccountsCard: 'https://hubspotaccountscard-gemiumdlca-uc.a.run.app',
    hubspotContactsCard: 'https://hubspotcontactscard-gemiumdlca-uc.a.run.app',
    hubspotDealsCard: 'https://hubspotdealscard-gemiumdlca-uc.a.run.app',
    hubspotListSequences:
      'https://hubspotlistsequences-gemiumdlca-uc.a.run.app',
    hubspotEnrollSequence:
      'https://hubspotenrollsequence-gemiumdlca-uc.a.run.app',

    generateAuthUrl: 'https://generateauthurl-gemiumdlca-uc.a.run.app',
    handleAuthCallback: 'https://handleauthcallback-gemiumdlca-uc.a.run.app',

    generateEmailAuthUrl:
      'https://generateemailauthurl-gemiumdlca-uc.a.run.app',
    handleEmailAuthCallback:
      'https://handleemailauthcallback-gemiumdlca-uc.a.run.app',
    sendUserEmail: 'https://senduseremail-gemiumdlca-uc.a.run.app',
    clearEmailOAuth: 'https://clearemailoauth-gemiumdlca-uc.a.run.app',
    messagingSend: 'https://messagingsend-gemiumdlca-uc.a.run.app',

    getTenantClaim: 'https://gettenantclaim-gemiumdlca-uc.a.run.app',
    setTenantClaim: 'https://settenantclaim-gemiumdlca-uc.a.run.app',
    createUserWithReset:
      'https://us-central1-topsailrevenue.cloudfunctions.net/createUserWithReset',
    bootstrapTenant: 'https://bootstraptenant-gemiumdlca-uc.a.run.app',
    grantSuperAdmin: '/grantSuperAdmin',
    getSuperadminPromptTemplateDefaults: '/getSuperadminPromptTemplateDefaults',
    listSuperadminTenants: '/listSuperadminTenants',
    updateTenantServiceOffering: '/updateTenantServiceOffering',
    listSuperadminMonthlyReports: '/listSuperadminMonthlyReports',
    getSuperadminTenantTaskOutcomeReport:
      '/getSuperadminTenantTaskOutcomeReport',
    getSuperadminTenantDealAttributionReport:
      '/getSuperadminTenantDealAttributionReport',
    requestForceLogoutAllUsers: '/requestForceLogoutAllUsers',
    listGuidanceCampaigns: '/listGuidanceCampaigns',
    saveGuidanceCampaignDraft: '/saveGuidanceCampaignDraft',
    publishGuidanceCampaign: '/publishGuidanceCampaign',
    archiveGuidanceCampaign: '/archiveGuidanceCampaign',
    deleteGuidanceCampaign: '/deleteGuidanceCampaign',
    resolveGuidanceCampaigns: '/resolveGuidanceCampaigns',
    recordGuidanceCampaignProgress: '/recordGuidanceCampaignProgress',
    setSuperadminPromptTemplateDefaults: '/setSuperadminPromptTemplateDefaults',
    createTenantDeletionPreview: '/createTenantDeletionPreview',
    getTenantDeletionJob: '/getTenantDeletionJob',
    confirmTenantQuarantine: '/confirmTenantQuarantine',
    startTenantPurge: '/startTenantPurge',

    initiateCall:
      'https://us-central1-topsailrevenue.cloudfunctions.net/twilioInitiateCall',
    systemCallInitiate:
      'https://us-central1-topsailrevenue.cloudfunctions.net/systemCallInitiate',
    voiceToken:
      'https://us-central1-topsailrevenue.cloudfunctions.net/twilioVoiceToken',
    logClientCall:
      'https://us-central1-topsailrevenue.cloudfunctions.net/twilioLogClientCall',
    focusmodeInitiate:
      'https://us-central1-topsailrevenue.cloudfunctions.net/twilioFocusmodeInitiate',
    focusmodeTerminate:
      'https://us-central1-topsailrevenue.cloudfunctions.net/twilioFocusmodeTerminate',

    getMeetingEngagements:
      'https://getmeetingengagements-gemiumdlca-uc.a.run.app',
    saveMeetingEngagement:
      'https://savemeetingengagement-gemiumdlca-uc.a.run.app',

    linkedInCompanyPeopleCSE:
      'https://linkedincompanypeoplecse-gemiumdlca-uc.a.run.app',
    callBrightDataScraperProfile:
      'https://callbrightdatascraperprofile-gemiumdlca-uc.a.run.app',

    getProducts: 'https://getproducts-gemiumdlca-uc.a.run.app',
    getCollectionStats: '/getCollectionStats',
    getBqMeta: '/getBqMeta',
    runBqReport: '/runBqReport',
    runMarketingAnalyticsDashboard: '/runMarketingAnalyticsDashboard',
    runMarketingAnalyticsBreakdown: '/runMarketingAnalyticsBreakdown',
    getMarketingNotificationSettings:
      'https://us-central1-topsailrevenue.cloudfunctions.net/getMarketingNotificationSettings',
    setMarketingNotificationSettings:
      'https://us-central1-topsailrevenue.cloudfunctions.net/setMarketingNotificationSettings',
    listSuperadminMarketingPosts: '/listSuperadminMarketingPosts',
    upsertSuperadminMarketingPost: '/upsertSuperadminMarketingPost',
    deleteSuperadminMarketingPost: '/deleteSuperadminMarketingPost',
    publishSuperadminMarketingPostNow: '/publishSuperadminMarketingPostNow',
    startSuperadminMarketingOAuth: '/startSuperadminMarketingOAuth',
    handleSuperadminMarketingOAuthCallback:
      '/handleSuperadminMarketingOAuthCallback',
    refreshSuperadminMarketingPostMetrics:
      '/refreshSuperadminMarketingPostMetrics',
    taskForecast: '/taskForecast',
    taskForecastRange: '/taskForecastRange',
    leadInboxCountsRange: '/leadInboxCountsRange',
  },

  development: {
    frontend: 'https://topsail-development.web.app',

    callPerplexityApiForAccounts:
      'https://callperplexityapiforaccounts-g5242ft6ia-uc.a.run.app',
    callPerplexityApiForAccountsStart:
      'https://callperplexityapiforaccountsstart-g5242ft6ia-uc.a.run.app',
    callPerplexityApiForAccountsCheck:
      'https://callperplexityapiforaccountscheck-g5242ft6ia-uc.a.run.app',

    generateInstallUrl: 'https://generateinstallurl-g5242ft6ia-uc.a.run.app',
    handleHubspotAppOAuth:
      'https://handlehubspotappoauth-g5242ft6ia-uc.a.run.app',

    getAccounts: 'https://getaccounts-g5242ft6ia-uc.a.run.app',
    getAccountsByOwner: 'https://getaccountsbyowner-g5242ft6ia-uc.a.run.app',
    getAccountDetails: 'https://getaccountdetails-g5242ft6ia-uc.a.run.app',
    getAccountDeals: 'https://getaccountdeals-g5242ft6ia-uc.a.run.app',
    updateAccountDetails:
      'https://updateaccountdetails-g5242ft6ia-uc.a.run.app',
    createCompany: 'https://createcompany-g5242ft6ia-uc.a.run.app',

    getContactDetails: 'https://getcontactdetails-g5242ft6ia-uc.a.run.app',
    updateContactDetails:
      'https://updatecontactdetails-g5242ft6ia-uc.a.run.app',
    getAccountContacts: 'https://getaccountcontacts-g5242ft6ia-uc.a.run.app',
    saveContactNotes: 'https://savecontactnotes-g5242ft6ia-uc.a.run.app',
    getContactNotes: 'https://getcontactnotes-g5242ft6ia-uc.a.run.app',
    saveContactReport: 'https://savecontactreport-g5242ft6ia-uc.a.run.app',
    createContact: 'https://createcontact-g5242ft6ia-uc.a.run.app',

    getDealDetails: 'https://getdealdetails-g5242ft6ia-uc.a.run.app',
    updateDealDetails: 'https://updatedealdetails-g5242ft6ia-uc.a.run.app',
    createDeal: 'https://createdeal-g5242ft6ia-uc.a.run.app',
    associateContactWithDeal:
      'https://associatecontactwithdeal-g5242ft6ia-uc.a.run.app',
    saveCompanyReport: 'https://savecompanyreport-g5242ft6ia-uc.a.run.app',
    saveHubspotContactReport:
      'https://savehubspotcontactreport-g5242ft6ia-uc.a.run.app',
    saveHubspotCompanyReport:
      'https://savehubspotcompanyreport-g5242ft6ia-uc.a.run.app',

    createLineItemForDeal:
      'https://createlineitemfordeal-g5242ft6ia-uc.a.run.app',
    updateLineItem: 'https://updatelineitem-g5242ft6ia-uc.a.run.app',
    deleteLineItem: 'https://deletelineitem-g5242ft6ia-uc.a.run.app',
    getLineItemsForDeal: 'https://getlineitemsfordeal-g5242ft6ia-uc.a.run.app',

    getPipelines: 'https://getpipelines-g5242ft6ia-uc.a.run.app',

    getUserTasks: 'https://getusertasks-g5242ft6ia-uc.a.run.app',
    manageUserTask: 'https://manageusertask-g5242ft6ia-uc.a.run.app',
    taskMutations: 'https://taskmutations-g5242ft6ia-uc.a.run.app',

    sendDailyNews: 'https://senddailynews-g5242ft6ia-uc.a.run.app',
    sendNewsForUser: 'https://sendnewsforuser-g5242ft6ia-uc.a.run.app',

    hubspotAccountsCard: 'https://hubspotaccountscard-g5242ft6ia-uc.a.run.app',
    hubspotContactsCard: 'https://hubspotcontactscard-g5242ft6ia-uc.a.run.app',
    hubspotDealsCard: 'https://hubspotdealscard-g5242ft6ia-uc.a.run.app',
    hubspotListSequences:
      'https://hubspotlistsequences-g5242ft6ia-uc.a.run.app',
    hubspotEnrollSequence:
      'https://hubspotenrollsequence-g5242ft6ia-uc.a.run.app',

    generateAuthUrl: 'https://generateauthurl-g5242ft6ia-uc.a.run.app',
    handleAuthCallback: 'https://handleauthcallback-g5242ft6ia-uc.a.run.app',

    generateEmailAuthUrl:
      'https://generateemailauthurl-g5242ft6ia-uc.a.run.app',
    handleEmailAuthCallback:
      'https://handleemailauthcallback-g5242ft6ia-uc.a.run.app',
    sendUserEmail: 'https://senduseremail-g5242ft6ia-uc.a.run.app',
    clearEmailOAuth: 'https://clearemailoauth-g5242ft6ia-uc.a.run.app',
    messagingSend: 'https://messagingsend-g5242ft6ia-uc.a.run.app',

    getTenantClaim: 'https://gettenantclaim-g5242ft6ia-uc.a.run.app',
    setTenantClaim: 'https://settenantclaim-g5242ft6ia-uc.a.run.app',
    createUserWithReset:
      'https://us-central1-topsail-development.cloudfunctions.net/createUserWithReset',
    bootstrapTenant: 'https://bootstraptenant-g5242ft6ia-uc.a.run.app',
    grantSuperAdmin: '/grantSuperAdmin',
    getSuperadminPromptTemplateDefaults: '/getSuperadminPromptTemplateDefaults',
    listSuperadminTenants: '/listSuperadminTenants',
    updateTenantServiceOffering: '/updateTenantServiceOffering',
    listSuperadminMonthlyReports: '/listSuperadminMonthlyReports',
    getSuperadminTenantTaskOutcomeReport:
      '/getSuperadminTenantTaskOutcomeReport',
    getSuperadminTenantDealAttributionReport:
      '/getSuperadminTenantDealAttributionReport',
    requestForceLogoutAllUsers: '/requestForceLogoutAllUsers',
    listGuidanceCampaigns: '/listGuidanceCampaigns',
    saveGuidanceCampaignDraft: '/saveGuidanceCampaignDraft',
    publishGuidanceCampaign: '/publishGuidanceCampaign',
    archiveGuidanceCampaign: '/archiveGuidanceCampaign',
    deleteGuidanceCampaign: '/deleteGuidanceCampaign',
    resolveGuidanceCampaigns: '/resolveGuidanceCampaigns',
    recordGuidanceCampaignProgress: '/recordGuidanceCampaignProgress',
    setSuperadminPromptTemplateDefaults: '/setSuperadminPromptTemplateDefaults',
    createTenantDeletionPreview: '/createTenantDeletionPreview',
    getTenantDeletionJob: '/getTenantDeletionJob',
    confirmTenantQuarantine: '/confirmTenantQuarantine',
    startTenantPurge: '/startTenantPurge',

    initiateCall:
      'https://us-central1-topsail-development.cloudfunctions.net/twilioInitiateCall',
    zoomCallInitiate:
      'https://us-central1-topsail-development.cloudfunctions.net/zoomCallInitiate',
    systemCallInitiate:
      'https://us-central1-topsail-development.cloudfunctions.net/systemCallInitiate',
    zoomStatus:
      'https://us-central1-topsail-development.cloudfunctions.net/zoomStatus',
    voiceToken:
      'https://us-central1-topsail-development.cloudfunctions.net/twilioVoiceToken',
    logClientCall:
      'https://us-central1-topsail-development.cloudfunctions.net/twilioLogClientCall',
    focusmodeInitiate:
      'https://us-central1-topsail-development.cloudfunctions.net/twilioFocusmodeInitiate',
    focusmodeTerminate:
      'https://us-central1-topsail-development.cloudfunctions.net/twilioFocusmodeTerminate',

    getMeetingEngagements:
      'https://getmeetingengagements-g5242ft6ia-uc.a.run.app',
    saveMeetingEngagement:
      'https://savemeetingengagement-g5242ft6ia-uc.a.run.app',

    linkedInCompanyPeopleCSE:
      'https://linkedincompanypeoplecse-g5242ft6ia-uc.a.run.app',
    callBrightDataScraperProfile:
      'https://callbrightdatascraperprofile-g5242ft6ia-uc.a.run.app',

    getProducts: 'https://getproducts-g5242ft6ia-uc.a.run.app',
    getCollectionStats: '/getCollectionStats',
    getBqMeta: '/getBqMeta',
    runBqReport: '/runBqReport',
    runMarketingAnalyticsDashboard: '/runMarketingAnalyticsDashboard',
    runMarketingAnalyticsBreakdown: '/runMarketingAnalyticsBreakdown',
    getMarketingNotificationSettings:
      'https://us-central1-topsail-development.cloudfunctions.net/getMarketingNotificationSettings',
    setMarketingNotificationSettings:
      'https://us-central1-topsail-development.cloudfunctions.net/setMarketingNotificationSettings',
    listSuperadminMarketingPosts: '/listSuperadminMarketingPosts',
    upsertSuperadminMarketingPost: '/upsertSuperadminMarketingPost',
    deleteSuperadminMarketingPost: '/deleteSuperadminMarketingPost',
    publishSuperadminMarketingPostNow:
      'https://us-central1-topsail-development.cloudfunctions.net/publishSuperadminMarketingPostNow',
    startSuperadminMarketingOAuth: '/startSuperadminMarketingOAuth',
    handleSuperadminMarketingOAuthCallback:
      '/handleSuperadminMarketingOAuthCallback',
    refreshSuperadminMarketingPostMetrics:
      '/refreshSuperadminMarketingPostMetrics',
    taskForecast: '/taskForecast',
    taskForecastRange: '/taskForecastRange',
    leadInboxCountsRange: '/leadInboxCountsRange',
  },

  /* ────────────────────────────
   * EAM Sandler project (topsail-eamsandler)
   * hash = 5mwexqqufq
   * ──────────────────────────── */
  eamSandler: {
    frontend: 'https://topsail-eamsandler.web.app',

    callPerplexityApiForAccounts:
      'https://callperplexityapiforaccounts-5mwexqqufq-uc.a.run.app',
    callPerplexityApiForAccountsStart:
      'https://callperplexityapiforaccountsstart-5mwexqqufq-uc.a.run.app',
    callPerplexityApiForAccountsCheck:
      'https://callperplexityapiforaccountscheck-5mwexqqufq-uc.a.run.app',

    generateInstallUrl: 'https://generateinstallurl-5mwexqqufq-uc.a.run.app',
    handleHubspotAppOAuth:
      'https://handlehubspotappoauth-5mwexqqufq-uc.a.run.app',

    getAccounts: 'https://getaccounts-5mwexqqufq-uc.a.run.app',
    getAccountsByOwner: 'https://getaccountsbyowner-5mwexqqufq-uc.a.run.app',
    getAccountDetails: 'https://getaccountdetails-5mwexqqufq-uc.a.run.app',
    getAccountDeals: 'https://getaccountdeals-5mwexqqufq-uc.a.run.app',
    updateAccountDetails:
      'https://updateaccountdetails-5mwexqqufq-uc.a.run.app',
    createCompany: 'https://createcompany-5mwexqqufq-uc.a.run.app',

    getContactDetails: 'https://getcontactdetails-5mwexqqufq-uc.a.run.app',
    updateContactDetails:
      'https://updatecontactdetails-5mwexqqufq-uc.a.run.app',
    getAccountContacts: 'https://getaccountcontacts-5mwexqqufq-uc.a.run.app',
    saveContactNotes: 'https://savecontactnotes-5mwexqqufq-uc.a.run.app',
    getContactNotes: 'https://getcontactnotes-5mwexqqufq-uc.a.run.app',
    saveContactReport: 'https://savecontactreport-5mwexqqufq-uc.a.run.app',
    createContact: 'https://createcontact-5mwexqqufq-uc.a.run.app',

    getDealDetails: 'https://getdealdetails-5mwexqqufq-uc.a.run.app',
    updateDealDetails: 'https://updatedealdetails-5mwexqqufq-uc.a.run.app',
    createDeal: 'https://createdeal-5mwexqqufq-uc.a.run.app',
    associateContactWithDeal:
      'https://associatecontactwithdeal-5mwexqqufq-uc.a.run.app',
    saveCompanyReport: 'https://savecompanyreport-5mwexqqufq-uc.a.run.app',
    saveHubspotContactReport:
      'https://savehubspotcontactreport-5mwexqqufq-uc.a.run.app',
    saveHubspotCompanyReport:
      'https://savehubspotcompanyreport-5mwexqqufq-uc.a.run.app',
    backfillHubspotPortalId:
      'https://backfillhubspotportalid-5mwexqqufq-uc.a.run.app',

    createLineItemForDeal:
      'https://createlineitemfordeal-5mwexqqufq-uc.a.run.app',
    updateLineItem: 'https://updatelineitem-5mwexqqufq-uc.a.run.app',
    deleteLineItem: 'https://deletelineitem-5mwexqqufq-uc.a.run.app',
    getLineItemsForDeal: 'https://getlineitemsfordeal-5mwexqqufq-uc.a.run.app',

    getPipelines: 'https://getpipelines-5mwexqqufq-uc.a.run.app',

    getUserTasks: 'https://getusertasks-5mwexqqufq-uc.a.run.app',
    manageUserTask: 'https://manageusertask-5mwexqqufq-uc.a.run.app',
    taskMutations: 'https://taskmutations-5mwexqqufq-uc.a.run.app',

    sendDailyNews: 'https://senddailynews-5mwexqqufq-uc.a.run.app',
    sendNewsForUser: 'https://sendnewsforuser-5mwexqqufq-uc.a.run.app',

    hubspotAccountsCard: 'https://hubspotaccountscard-5mwexqqufq-uc.a.run.app',
    hubspotContactsCard: 'https://hubspotcontactscard-5mwexqqufq-uc.a.run.app',
    hubspotDealsCard: 'https://hubspotdealscard-5mwexqqufq-uc.a.run.app',
    hubspotListSequences:
      'https://hubspotlistsequences-5mwexqqufq-uc.a.run.app',
    hubspotEnrollSequence:
      'https://hubspotenrollsequence-5mwexqqufq-uc.a.run.app',

    generateAuthUrl: 'https://generateauthurl-5mwexqqufq-uc.a.run.app',
    handleAuthCallback: 'https://handleauthcallback-5mwexqqufq-uc.a.run.app',

    generateEmailAuthUrl:
      'https://generateemailauthurl-5mwexqqufq-uc.a.run.app',
    handleEmailAuthCallback:
      'https://handleemailauthcallback-5mwexqqufq-uc.a.run.app',
    sendUserEmail: 'https://senduseremail-5mwexqqufq-uc.a.run.app',
    clearEmailOAuth: 'https://clearemailoauth-5mwexqqufq-uc.a.run.app',
    messagingSend: 'https://messagingsend-5mwexqqufq-uc.a.run.app',

    getTenantClaim: 'https://gettenantclaim-5mwexqqufq-uc.a.run.app',
    setTenantClaim: 'https://settenantclaim-5mwexqqufq-uc.a.run.app',
    createUserWithReset:
      'https://us-central1-topsail-eamsandler.cloudfunctions.net/createUserWithReset',
    bootstrapTenant: 'https://bootstraptenant-5mwexqqufq-uc.a.run.app',
    grantSuperAdmin: '/grantSuperAdmin',
    getSuperadminPromptTemplateDefaults: '/getSuperadminPromptTemplateDefaults',
    listSuperadminTenants: '/listSuperadminTenants',
    updateTenantServiceOffering: '/updateTenantServiceOffering',
    listSuperadminMonthlyReports: '/listSuperadminMonthlyReports',
    getSuperadminTenantTaskOutcomeReport:
      '/getSuperadminTenantTaskOutcomeReport',
    getSuperadminTenantDealAttributionReport:
      '/getSuperadminTenantDealAttributionReport',
    requestForceLogoutAllUsers: '/requestForceLogoutAllUsers',
    listGuidanceCampaigns: '/listGuidanceCampaigns',
    saveGuidanceCampaignDraft: '/saveGuidanceCampaignDraft',
    publishGuidanceCampaign: '/publishGuidanceCampaign',
    archiveGuidanceCampaign: '/archiveGuidanceCampaign',
    deleteGuidanceCampaign: '/deleteGuidanceCampaign',
    resolveGuidanceCampaigns: '/resolveGuidanceCampaigns',
    recordGuidanceCampaignProgress: '/recordGuidanceCampaignProgress',
    setSuperadminPromptTemplateDefaults: '/setSuperadminPromptTemplateDefaults',
    createTenantDeletionPreview: '/createTenantDeletionPreview',
    getTenantDeletionJob: '/getTenantDeletionJob',
    confirmTenantQuarantine: '/confirmTenantQuarantine',
    startTenantPurge: '/startTenantPurge',

    initiateCall:
      'https://us-central1-topsail-eamsandler.cloudfunctions.net/twilioInitiateCall',
    zoomCallInitiate:
      'https://us-central1-topsail-eamsandler.cloudfunctions.net/zoomCallInitiate',
    systemCallInitiate:
      'https://us-central1-topsail-eamsandler.cloudfunctions.net/systemCallInitiate',
    zoomStatus:
      'https://us-central1-topsail-eamsandler.cloudfunctions.net/zoomStatus',
    voiceToken:
      'https://us-central1-topsail-eamsandler.cloudfunctions.net/twilioVoiceToken',
    logClientCall:
      'https://us-central1-topsail-eamsandler.cloudfunctions.net/twilioLogClientCall',
    focusmodeInitiate:
      'https://us-central1-topsail-eamsandler.cloudfunctions.net/twilioFocusmodeInitiate',

    focusmodeTerminate:
      'https://us-central1-topsail-eamsandler.cloudfunctions.net/twilioFocusmodeTerminate',

    getMeetingEngagements:
      'https://getmeetingengagements-5mwexqqufq-uc.a.run.app',
    saveMeetingEngagement:
      'https://savemeetingengagement-5mwexqqufq-uc.a.run.app',

    linkedInCompanyPeopleCSE:
      'https://linkedincompanypeoplecse-5mwexqqufq-uc.a.run.app',
    callBrightDataScraperProfile:
      'https://callbrightdatascraperprofile-5mwexqqufq-uc.a.run.app',

    getProducts: 'https://getproducts-5mwexqqufq-uc.a.run.app',
    getCollectionStats: '/getCollectionStats',
    getBqMeta: '/getBqMeta',
    runBqReport: '/runBqReport',
    runMarketingAnalyticsDashboard: '/runMarketingAnalyticsDashboard',
    runMarketingAnalyticsBreakdown: '/runMarketingAnalyticsBreakdown',
    getMarketingNotificationSettings:
      'https://us-central1-topsail-eamsandler.cloudfunctions.net/getMarketingNotificationSettings',
    setMarketingNotificationSettings:
      'https://us-central1-topsail-eamsandler.cloudfunctions.net/setMarketingNotificationSettings',
    listSuperadminMarketingPosts: '/listSuperadminMarketingPosts',
    upsertSuperadminMarketingPost: '/upsertSuperadminMarketingPost',
    deleteSuperadminMarketingPost: '/deleteSuperadminMarketingPost',
    publishSuperadminMarketingPostNow: '/publishSuperadminMarketingPostNow',
    startSuperadminMarketingOAuth: '/startSuperadminMarketingOAuth',
    handleSuperadminMarketingOAuthCallback:
      '/handleSuperadminMarketingOAuthCallback',
    refreshSuperadminMarketingPostMetrics:
      '/refreshSuperadminMarketingPostMetrics',
    taskForecast: '/taskForecast',
    taskForecastRange: '/taskForecastRange',
    leadInboxCountsRange: '/leadInboxCountsRange',
  },

  /* ────────────────────────────
   * acuteIncite project (topsail-acuteincite)
   * hash = leh2kkxquq
   * ──────────────────────────── */
  acuteIncite: {
    frontend: 'https://topsail-acuteincite.web.app',

    callPerplexityApiForAccounts:
      'https://callperplexityapiforaccounts-leh2kkxquq-uc.a.run.app',
    callPerplexityApiForAccountsStart:
      'https://callperplexityapiforaccountsstart-leh2kkxquq-uc.a.run.app',
    callPerplexityApiForAccountsCheck:
      'https://callperplexityapiforaccountscheck-leh2kkxquq-uc.a.run.app',

    generateInstallUrl: 'https://generateinstallurl-leh2kkxquq-uc.a.run.app',
    handleHubspotAppOAuth:
      'https://handlehubspotappoauth-leh2kkxquq-uc.a.run.app',

    getAccounts: 'https://getaccounts-leh2kkxquq-uc.a.run.app',
    getAccountsByOwner: 'https://getaccountsbyowner-leh2kkxquq-uc.a.run.app',
    getAccountDetails: 'https://getaccountdetails-leh2kkxquq-uc.a.run.app',
    getAccountDeals: 'https://getaccountdeals-leh2kkxquq-uc.a.run.app',
    updateAccountDetails:
      'https://updateaccountdetails-leh2kkxquq-uc.a.run.app',
    createCompany: 'https://createcompany-leh2kkxquq-uc.a.run.app',

    getContactDetails: 'https://getcontactdetails-leh2kkxquq-uc.a.run.app',
    updateContactDetails:
      'https://updatecontactdetails-leh2kkxquq-uc.a.run.app',
    getAccountContacts: 'https://getaccountcontacts-leh2kkxquq-uc.a.run.app',
    saveContactNotes: 'https://savecontactnotes-leh2kkxquq-uc.a.run.app',
    getContactNotes: 'https://getcontactnotes-leh2kkxquq-uc.a.run.app',
    saveContactReport: 'https://savecontactreport-leh2kkxquq-uc.a.run.app',
    createContact: 'https://createcontact-leh2kkxquq-uc.a.run.app',

    getDealDetails: 'https://getdealdetails-leh2kkxquq-uc.a.run.app',
    updateDealDetails: 'https://updatedealdetails-leh2kkxquq-uc.a.run.app',
    createDeal: 'https://createdeal-leh2kkxquq-uc.a.run.app',
    associateContactWithDeal:
      'https://associatecontactwithdeal-leh2kkxquq-uc.a.run.app',
    saveCompanyReport: 'https://savecompanyreport-leh2kkxquq-uc.a.run.app',
    saveHubspotContactReport:
      'https://savehubspotcontactreport-leh2kkxquq-uc.a.run.app',
    saveHubspotCompanyReport:
      'https://savehubspotcompanyreport-leh2kkxquq-uc.a.run.app',
    backfillHubspotPortalId:
      'https://backfillhubspotportalid-leh2kkxquq-uc.a.run.app',

    createLineItemForDeal:
      'https://createlineitemfordeal-leh2kkxquq-uc.a.run.app',
    updateLineItem: 'https://updatelineitem-leh2kkxquq-uc.a.run.app',
    deleteLineItem: 'https://deletelineitem-leh2kkxquq-uc.a.run.app',
    getLineItemsForDeal: 'https://getlineitemsfordeal-leh2kkxquq-uc.a.run.app',

    getPipelines: 'https://getpipelines-leh2kkxquq-uc.a.run.app',

    getUserTasks: 'https://getusertasks-leh2kkxquq-uc.a.run.app',
    manageUserTask: 'https://manageusertask-leh2kkxquq-uc.a.run.app',
    taskMutations: 'https://taskmutations-leh2kkxquq-uc.a.run.app',

    sendDailyNews: 'https://senddailynews-leh2kkxquq-uc.a.run.app',
    sendNewsForUser: 'https://sendnewsforuser-leh2kkxquq-uc.a.run.app',

    hubspotAccountsCard: 'https://hubspotaccountscard-leh2kkxquq-uc.a.run.app',
    hubspotContactsCard: 'https://hubspotcontactscard-leh2kkxquq-uc.a.run.app',
    hubspotDealsCard: 'https://hubspotdealscard-leh2kkxquq-uc.a.run.app',
    hubspotListSequences:
      'https://hubspotlistsequences-leh2kkxquq-uc.a.run.app',
    hubspotEnrollSequence:
      'https://hubspotenrollsequence-leh2kkxquq-uc.a.run.app',

    generateAuthUrl: 'https://generateauthurl-leh2kkxquq-uc.a.run.app',
    handleAuthCallback: 'https://handleauthcallback-leh2kkxquq-uc.a.run.app',

    generateEmailAuthUrl:
      'https://generateemailauthurl-leh2kkxquq-uc.a.run.app',
    handleEmailAuthCallback:
      'https://handleemailauthcallback-leh2kkxquq-uc.a.run.app',
    sendUserEmail: 'https://senduseremail-leh2kkxquq-uc.a.run.app',
    clearEmailOAuth: 'https://clearemailoauth-leh2kkxquq-uc.a.run.app',
    messagingSend: 'https://messagingsend-leh2kkxquq-uc.a.run.app',

    getTenantClaim: 'https://gettenantclaim-leh2kkxquq-uc.a.run.app',
    setTenantClaim: 'https://settenantclaim-leh2kkxquq-uc.a.run.app',
    createUserWithReset:
      'https://us-central1-topsail-acuteincite.cloudfunctions.net/createUserWithReset',
    bootstrapTenant: 'https://bootstraptenant-leh2kkxquq-uc.a.run.app',
    grantSuperAdmin: '/grantSuperAdmin',
    getSuperadminPromptTemplateDefaults: '/getSuperadminPromptTemplateDefaults',
    listSuperadminTenants: '/listSuperadminTenants',
    updateTenantServiceOffering: '/updateTenantServiceOffering',
    requestForceLogoutAllUsers: '/requestForceLogoutAllUsers',
    listGuidanceCampaigns: '/listGuidanceCampaigns',
    saveGuidanceCampaignDraft: '/saveGuidanceCampaignDraft',
    publishGuidanceCampaign: '/publishGuidanceCampaign',
    archiveGuidanceCampaign: '/archiveGuidanceCampaign',
    deleteGuidanceCampaign: '/deleteGuidanceCampaign',
    resolveGuidanceCampaigns: '/resolveGuidanceCampaigns',
    recordGuidanceCampaignProgress: '/recordGuidanceCampaignProgress',
    setSuperadminPromptTemplateDefaults: '/setSuperadminPromptTemplateDefaults',
    createTenantDeletionPreview: '/createTenantDeletionPreview',
    getTenantDeletionJob: '/getTenantDeletionJob',
    confirmTenantQuarantine: '/confirmTenantQuarantine',
    startTenantPurge: '/startTenantPurge',

    initiateCall:
      'https://us-central1-topsail-acuteincite.cloudfunctions.net/twilioInitiateCall',
    zoomCallInitiate:
      'https://us-central1-topsail-acuteincite.cloudfunctions.net/zoomCallInitiate',
    systemCallInitiate:
      'https://us-central1-topsail-acuteincite.cloudfunctions.net/systemCallInitiate',
    zoomStatus:
      'https://us-central1-topsail-acuteincite.cloudfunctions.net/zoomStatus',
    voiceToken:
      'https://us-central1-topsail-acuteincite.cloudfunctions.net/twilioVoiceToken',
    logClientCall:
      'https://us-central1-topsail-acuteincite.cloudfunctions.net/twilioLogClientCall',
    focusmodeInitiate:
      'https://us-central1-topsail-acuteincite.cloudfunctions.net/twilioFocusmodeInitiate',
    focusmodeTerminate:
      'https://us-central1-topsail-acuteincite.cloudfunctions.net/twilioFocusmodeTerminate',

    getMeetingEngagements:
      'https://getmeetingengagements-leh2kkxquq-uc.a.run.app',
    saveMeetingEngagement:
      'https://savemeetingengagement-leh2kkxquq-uc.a.run.app',

    linkedInCompanyPeopleCSE:
      'https://linkedincompanypeoplecse-leh2kkxquq-uc.a.run.app',
    callBrightDataScraperProfile:
      'https://callbrightdatascraperprofile-leh2kkxquq-uc.a.run.app',

    getProducts: 'https://getproducts-leh2kkxquq-uc.a.run.app',
    getCollectionStats: '/getCollectionStats',
    getBqMeta: '/getBqMeta',
    runBqReport: '/runBqReport',
    runMarketingAnalyticsDashboard: '/runMarketingAnalyticsDashboard',
    runMarketingAnalyticsBreakdown: '/runMarketingAnalyticsBreakdown',
    getMarketingNotificationSettings:
      'https://us-central1-topsail-acuteincite.cloudfunctions.net/getMarketingNotificationSettings',
    setMarketingNotificationSettings:
      'https://us-central1-topsail-acuteincite.cloudfunctions.net/setMarketingNotificationSettings',
    listSuperadminMarketingPosts: '/listSuperadminMarketingPosts',
    upsertSuperadminMarketingPost: '/upsertSuperadminMarketingPost',
    deleteSuperadminMarketingPost: '/deleteSuperadminMarketingPost',
    publishSuperadminMarketingPostNow: '/publishSuperadminMarketingPostNow',
    startSuperadminMarketingOAuth: '/startSuperadminMarketingOAuth',
    handleSuperadminMarketingOAuthCallback:
      '/handleSuperadminMarketingOAuthCallback',
    refreshSuperadminMarketingPostMetrics:
      '/refreshSuperadminMarketingPostMetrics',
    taskForecast: '/taskForecast',
    taskForecastRange: '/taskForecastRange',
    leadInboxCountsRange: '/leadInboxCountsRange',
  },
};

// --- map Firebase projectId → envRoots key ------------------------------
const PROJECT_ENV_MAP = {
  topsailrevenue: 'production',
  'topsail-eamsandler': 'eamSandler',
  'topsail-acuteincite': 'acuteIncite',
  'topsail-development': 'development',
  // add more when you spin up new projects
};

function defaultDetectEnvironment() {
  /* 1️⃣ Explicit ?env=QS override (always wins) */
  const qsEnv = new URLSearchParams(location.search).get('env')?.toLowerCase();
  if (qsEnv && envRoots[qsEnv]) return qsEnv;

  /* 2️⃣ Hostname heuristics */
  const host = location.hostname.toLowerCase();
  if (/^topsailrevenue(--.+)?\.web\.app$/.test(host)) return 'production';
  if (/^topsail-eamsandler(--.+)?\.web\.app$/.test(host)) return 'eamSandler';
  if (/^topsail-acuteincite(--.+)?\.web\.app$/.test(host)) return 'acuteIncite';

  if (/^topsail-development(--.+)?\.web\.app$/.test(host)) return 'development';
  /* 3️⃣ Firebase project-id (fallback) */
  let firebaseProjectId;
  if (window.firebaseConfig?.projectId) {
    const runtimeApp =
      getApps().find((a) => a.name === 'topsail-runtime') ??
      initializeApp(window.firebaseConfig, 'topsail-runtime');
    firebaseProjectId = runtimeApp.options.projectId;
  }

  if (PROJECT_ENV_MAP[firebaseProjectId])
    return PROJECT_ENV_MAP[firebaseProjectId];

  /* 4️⃣ Default catch-all */
  return 'dev';
}

/**
 * Build a URL and serialise query-string parameters.
 *
 * @param {string} base             Absolute or relative URL root
 * @param {Record<string, any>} params  Key-value map (values coerced to string;
 *                                      undefined / null are skipped)
 * @returns {string} Fully-formed URL
 */
function defaultBuildUrl(base, params = {}) {
  if (!base) {
    throw new Error('Endpoint base URL is required');
  }
  const url = new URL(base, window.location.origin); // handles “/path” bases
  for (const [key, value] of Object.entries(params)) {
    if (value != null) url.searchParams.set(key, String(value));
  }
  return url.toString();
}

/**
 * Determine whether the clearEmailOAuth function should be invoked via the
 * Firebase Hosting rewrite so that the browser call stays on-origin.
 *
 * @returns {boolean} true when the current window location is a Firebase hosted origin
 */
function shouldUseClearEmailOAuthRewrite() {
  if (typeof window === 'undefined' || !window.location?.origin) return false;

  try {
    const { hostname } = new URL(window.location.origin);
    return (
      hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com')
    );
  } catch (err) {
    return false;
  }
}

/**
 * Resolve a fetch implementation for browser and test environments.
 *
 * @returns {Function} Bound fetch-like function.
 */
function resolveDefaultFetchClient() {
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    return window.fetch.bind(window);
  }

  if (typeof globalThis.fetch === 'function') {
    return globalThis.fetch.bind(globalThis);
  }

  return async function missingFetchClient() {
    throw new Error('Fetch API is not available');
  };
}

/**
 * EndpointService class
 * @param {Object} config
 * @param {Object} config.envRoots       - map of environments to root URLs
 * @param {Function} [config.detectEnvironment]
 * @param {Function} [config.buildUrl]
 * @param {Function} [config.fetchClient]
 */
export class EndpointService {
  constructor({
    envRoots,
    detectEnvironment = defaultDetectEnvironment,
    buildUrl = defaultBuildUrl,
    fetchClient = resolveDefaultFetchClient(),
  }) {
    this.fetchClient = fetchClient;
    this.buildUrl = buildUrl;
    this.env = detectEnvironment();
    this.roots = { ...(envRoots[this.env] || envRoots.dev) };

    if (shouldUseClearEmailOAuthRewrite()) {
      this.roots.clearEmailOAuth = '/clearEmailOAuth';
    }
  }

  /**
   * Build a link into your front‑end app, with optional query params.
   * @param {string} path    e.g. "/contact.html"
   * @param {object} params  e.g. { contactId, accountId }
   * @returns {string} full URL
   */
  getFrontendUrl(path, params = {}) {
    // strip any trailing slash off the root
    const root = this.roots.frontend.replace(/\/+$/, '');
    const url = new URL(root + path);
    Object.entries(params).forEach(([k, v]) => {
      if (v != null) url.searchParams.set(k, String(v));
    });
    return url.toString();
  }

  /**
   * Wrapper around fetchClient for uniform usage
   * @param {string} url
   * @param {RequestInit} [opts]
   * @returns {Promise<Response>}
   */
  /** Uniform fetch wrapper */
  fetch(url, opts) {
    return this.fetchClient(url, opts);
  }

  // === Query‑param endpoints ===
  getAccounts(opts) {
    return this.buildUrl(this.roots.getAccounts, opts);
  }
  getAccountsByOwner(opts) {
    return this.buildUrl(this.roots.getAccountsByOwner, opts);
  }
  getAccountDetails(opts) {
    return this.buildUrl(this.roots.getAccountDetails, opts);
  }
  getAccountDeals(opts) {
    return this.buildUrl(this.roots.getAccountDeals, opts);
  }
  updateAccountDetails(opts) {
    return this.buildUrl(this.roots.updateAccountDetails, opts);
  }
  createCompany(opts) {
    return this.buildUrl(this.roots.createCompany, opts);
  }

  getContactDetails(opts) {
    return this.buildUrl(this.roots.getContactDetails, opts);
  }
  updateContactDetails(opts) {
    return this.buildUrl(this.roots.updateContactDetails, opts);
  }
  getAccountContacts(opts) {
    return this.buildUrl(this.roots.getAccountContacts, opts);
  }
  /** Route contact note writes. Pass `{ crm: 'local' }` for mirror storage. */
  saveContactNotes(opts = {}) {
    const { userId, contactId, noteId = '', crm, tenantId } = opts;

    if (!userId || !contactId || !crm) {
      throw new Error('saveContactNotes: missing args');
    }

    if (crm !== 'local' && !tenantId) {
      throw new Error('saveContactNotes: tenantId required');
    }

    return this.buildUrl(this.roots.saveContactNotes, {
      userId,
      contactId,
      noteId,
      crm,
      tenantId,
      tenant: tenantId,
    });
  }
  /** Route contact note reads. Pass `{ crm: 'local' }` for mirror reads. */
  getContactNotes(opts = {}) {
    const {
      userId,
      contactId,
      crm,
      tenantId,
      includeActivities = false,
    } = opts;

    if (!userId || !contactId || !crm) {
      throw new Error('getContactNotes: missing args');
    }

    if (crm !== 'local' && !tenantId) {
      throw new Error('getContactNotes: tenantId required');
    }

    return this.buildUrl(this.roots.getContactNotes, {
      userId,
      contactId,
      crm,
      tenantId,
      tenant: tenantId,
      ...(includeActivities && { includeActivities: 'true' }),
    });
  }
  saveContactReport(opts) {
    return this.buildUrl(this.roots.saveContactReport, opts);
  }
  createContact(opts) {
    return this.buildUrl(this.roots.createContact, opts);
  }

  getDealDetails(opts) {
    return this.buildUrl(this.roots.getDealDetails, opts);
  }
  updateDealDetails(opts) {
    return this.buildUrl(this.roots.updateDealDetails, opts);
  }
  createDeal(opts) {
    return this.buildUrl(this.roots.createDeal, opts);
  }
  associateContactWithDeal(opts) {
    return this.buildUrl(this.roots.associateContactWithDeal, opts);
  }

  getLineItemsForDeal(opts) {
    return this.buildUrl(this.roots.getLineItemsForDeal, opts);
  }
  createLineItemForDeal(opts) {
    return this.buildUrl(this.roots.createLineItemForDeal, opts);
  }
  updateLineItem(opts) {
    return this.buildUrl(this.roots.updateLineItem, opts);
  }
  deleteLineItem(opts) {
    return this.buildUrl(this.roots.deleteLineItem, opts);
  }

  getPipelines(opts) {
    return this.buildUrl(this.roots.getPipelines, opts);
  }

  getUserTasks(opts) {
    return this.buildUrl(this.roots.getUserTasks, opts);
  }
  manageUserTask(opts) {
    return this.buildUrl(this.roots.manageUserTask, opts);
  }
  taskMutations(opts) {
    return this.buildUrl(this.roots.taskMutations, opts);
  }

  sendDailyNews(opts) {
    return this.buildUrl(this.roots.sendDailyNews, opts);
  }
  sendNewsForUser(opts) {
    return this.buildUrl(this.roots.sendNewsForUser, opts);
  }

  hubspotAccountsCard(opts) {
    return this.buildUrl(this.roots.hubspotAccountsCard, opts);
  }
  hubspotContactsCard(opts) {
    return this.buildUrl(this.roots.hubspotContactsCard, opts);
  }
  hubspotDealsCard(opts) {
    return this.buildUrl(this.roots.hubspotDealsCard, opts);
  }
  hubspotListSequences(opts) {
    return this.buildUrl(this.roots.hubspotListSequences, opts);
  }
  hubspotEnrollSequence(opts) {
    return this.buildUrl(this.roots.hubspotEnrollSequence, opts);
  }

  generateInstallUrl(opts) {
    return this.buildUrl(this.roots.generateInstallUrl, opts);
  }
  handleHubspotAppOAuth(opts) {
    return this.buildUrl(this.roots.handleHubspotAppOAuth, opts);
  }
  generateAuthUrl(opts) {
    return this.buildUrl(this.roots.generateAuthUrl, opts);
  }
  handleAuthCallback(opts) {
    return this.buildUrl(this.roots.handleAuthCallback, opts);
  }
  generateEmailAuthUrl(opts) {
    return this.buildUrl(this.roots.generateEmailAuthUrl, opts);
  }
  handleEmailAuthCallback(opts) {
    return this.buildUrl(this.roots.handleEmailAuthCallback, opts);
  }
  sendUserEmail(opts) {
    return this.buildUrl(this.roots.sendUserEmail, opts);
  }
  clearEmailOAuth(opts) {
    const remoteUrl = this.buildUrl(this.roots.clearEmailOAuth, opts);

    if (typeof window !== 'undefined' && window.location?.origin) {
      try {
        const { origin } = window.location;
        const host = new URL(origin).hostname;

        if (host.endsWith('.web.app') || host.endsWith('.firebaseapp.com')) {
          return new URL('/clearEmailOAuth', origin).toString();
        }
      } catch (err) {
        // If parsing fails fall back to the remote function URL.
      }
    }

    return remoteUrl;
  }
  messagingSend(opts) {
    return this.buildUrl(this.roots.messagingSend, opts);
  }

  getMeetingEngagements(opts) {
    return this.buildUrl(this.roots.getMeetingEngagements, opts);
  }
  saveMeetingEngagement(opts) {
    return this.buildUrl(this.roots.saveMeetingEngagement, opts);
  }

  linkedInCompanyPeopleCSE(opts) {
    return this.buildUrl(this.roots.linkedInCompanyPeopleCSE, opts);
  }
  callBrightDataScraperProfile(opts) {
    return this.buildUrl(this.roots.callBrightDataScraperProfile, opts);
  }

  getProducts(opts) {
    return this.buildUrl(this.roots.getProducts, opts);
  }
  getCollectionStats(opts) {
    return this.buildUrl(this.roots.getCollectionStats, opts);
  }
  getBqMeta(opts) {
    return this.buildUrl(this.roots.getBqMeta, opts);
  }
  runBqReport(opts) {
    return this.buildUrl(this.roots.runBqReport, opts);
  }
  runMarketingAnalyticsDashboard(opts) {
    return this.buildUrl(this.roots.runMarketingAnalyticsDashboard, opts);
  }
  runMarketingAnalyticsBreakdown(opts) {
    return this.buildUrl(this.roots.runMarketingAnalyticsBreakdown, opts);
  }
  getMarketingNotificationSettings(opts) {
    return this.buildUrl(this.roots.getMarketingNotificationSettings, opts);
  }
  setMarketingNotificationSettings(opts) {
    return this.buildUrl(this.roots.setMarketingNotificationSettings, opts);
  }
  listSuperadminMarketingPosts(opts) {
    return this.buildUrl(this.roots.listSuperadminMarketingPosts, opts);
  }
  upsertSuperadminMarketingPost(opts) {
    return this.buildUrl(this.roots.upsertSuperadminMarketingPost, opts);
  }
  deleteSuperadminMarketingPost(opts) {
    return this.buildUrl(this.roots.deleteSuperadminMarketingPost, opts);
  }
  publishSuperadminMarketingPostNow(opts) {
    return this.buildUrl(this.roots.publishSuperadminMarketingPostNow, opts);
  }
  startSuperadminMarketingOAuth(opts) {
    return this.buildUrl(this.roots.startSuperadminMarketingOAuth, opts);
  }
  handleSuperadminMarketingOAuthCallback(opts) {
    return this.buildUrl(
      this.roots.handleSuperadminMarketingOAuthCallback,
      opts
    );
  }
  refreshSuperadminMarketingPostMetrics(opts) {
    return this.buildUrl(
      this.roots.refreshSuperadminMarketingPostMetrics,
      opts
    );
  }
  taskForecast(opts) {
    return this.buildUrl(this.roots.taskForecast, opts);
  }
  taskForecastRange(opts) {
    return this.buildUrl(this.roots.taskForecastRange, opts);
  }
  leadInboxCountsRange(opts) {
    return this.buildUrl(this.roots.leadInboxCountsRange, opts);
  }

  callPerplexityApiForAccounts(opts) {
    return this.buildUrl(this.roots.callPerplexityApiForAccounts, opts);
  }
  callPerplexityApiForAccountsStart(opts) {
    return this.buildUrl(this.roots.callPerplexityApiForAccountsStart, opts);
  }
  callPerplexityApiForAccountsCheck(opts) {
    return this.buildUrl(this.roots.callPerplexityApiForAccountsCheck, opts);
  }

  // AI‑company report now also builds its querystring
  saveCompanyReport(opts) {
    return this.buildUrl(this.roots.saveCompanyReport, opts);
  }
  saveHubspotContactReport(opts) {
    return this.buildUrl(this.roots.saveHubspotContactReport, opts);
  }
  saveHubspotCompanyReport(opts) {
    return this.buildUrl(this.roots.saveHubspotCompanyReport, opts);
  }
  backfillHubspotPortalId(opts) {
    return this.buildUrl(this.roots.backfillHubspotPortalId, opts);
  }

  getTenantClaim(opts) {
    return this.buildUrl(this.roots.getTenantClaim, opts);
  }
  setTenantClaim(opts) {
    return this.buildUrl(this.roots.setTenantClaim, opts);
  }
  createUserWithReset(opts) {
    return this.buildUrl(this.roots.createUserWithReset, opts);
  }
  bootstrapTenant(opts) {
    return this.buildUrl(this.roots.bootstrapTenant, opts);
  }
  grantSuperAdmin(opts) {
    return this.buildUrl(this.roots.grantSuperAdmin, opts);
  }
  getSuperadminPromptTemplateDefaults(opts) {
    return this.buildUrl(this.roots.getSuperadminPromptTemplateDefaults, opts);
  }
  listSuperadminTenants(opts) {
    return this.buildUrl(this.roots.listSuperadminTenants, opts);
  }
  /**
   * Build the endpoint URL for updating a tenant service offering.
   *
   * @param {Record<string, any>} [opts] - Optional URL build settings.
   * @returns {string} Resolved endpoint URL.
   */
  updateTenantServiceOffering(opts) {
    return this.buildUrl(this.roots.updateTenantServiceOffering, opts);
  }
  listSuperadminMonthlyReports(opts) {
    return this.buildUrl(this.roots.listSuperadminMonthlyReports, opts);
  }
  getSuperadminTenantTaskOutcomeReport(opts) {
    return this.buildUrl(this.roots.getSuperadminTenantTaskOutcomeReport, opts);
  }
  getSuperadminTenantDealAttributionReport(opts) {
    return this.buildUrl(
      this.roots.getSuperadminTenantDealAttributionReport ||
        '/getSuperadminTenantDealAttributionReport',
      opts
    );
  }
  getSuperadminFocusModePerformanceReport(opts) {
    return this.buildUrl(
      this.roots.getSuperadminFocusModePerformanceReport ||
        '/getSuperadminFocusModePerformanceReport',
      opts
    );
  }
  requestForceLogoutAllUsers(opts) {
    return this.buildUrl(this.roots.requestForceLogoutAllUsers, opts);
  }
  listGuidanceCampaigns(opts) {
    return this.buildUrl(this.roots.listGuidanceCampaigns, opts);
  }
  saveGuidanceCampaignDraft(opts) {
    return this.buildUrl(this.roots.saveGuidanceCampaignDraft, opts);
  }
  publishGuidanceCampaign(opts) {
    return this.buildUrl(this.roots.publishGuidanceCampaign, opts);
  }
  archiveGuidanceCampaign(opts) {
    return this.buildUrl(this.roots.archiveGuidanceCampaign, opts);
  }
  deleteGuidanceCampaign(opts) {
    return this.buildUrl(this.roots.deleteGuidanceCampaign, opts);
  }
  resolveGuidanceCampaigns(opts) {
    return this.buildUrl(this.roots.resolveGuidanceCampaigns, opts);
  }
  recordGuidanceCampaignProgress(opts) {
    return this.buildUrl(this.roots.recordGuidanceCampaignProgress, opts);
  }
  setSuperadminPromptTemplateDefaults(opts) {
    return this.buildUrl(this.roots.setSuperadminPromptTemplateDefaults, opts);
  }
  createTenantDeletionPreview(opts) {
    return this.buildUrl(this.roots.createTenantDeletionPreview, opts);
  }
  getTenantDeletionJob(opts) {
    return this.buildUrl(this.roots.getTenantDeletionJob, opts);
  }
  confirmTenantQuarantine(opts) {
    return this.buildUrl(this.roots.confirmTenantQuarantine, opts);
  }
  startTenantPurge(opts) {
    return this.buildUrl(this.roots.startTenantPurge, opts);
  }

  initiateCall(opts) {
    return this.buildUrl(this.roots.initiateCall, opts);
  }
  zoomCallInitiate(opts) {
    return this.buildUrl(this.roots.zoomCallInitiate, opts);
  }
  systemCallInitiate(opts) {
    return this.buildUrl(this.roots.systemCallInitiate, opts);
  }
  zoomStatus(opts) {
    return this.buildUrl(this.roots.zoomStatus, opts);
  }
  voiceToken(opts) {
    return this.buildUrl(this.roots.voiceToken, opts);
  }
  logClientCall(opts) {
    return this.buildUrl(this.roots.logClientCall, opts);
  }
  focusmodeInitiate(opts) {
    return this.buildUrl(this.roots.focusmodeInitiate, opts);
  }
  focusmodeTerminate(opts) {
    return this.buildUrl(this.roots.focusmodeTerminate, opts);
  }
}

// 3) Default instance for direct imports
const endpoints = new EndpointService({ envRoots });
endpoints.schema = {
  collections: '/schema/collections',
  fields: ({ collection }) => `/schema/fields?collection=${collection}`,
  distinct: ({ collection, field }) =>
    `/schema/distinct?collection=${collection}&field=${field}`,
};
export default endpoints;
